import React, { useEffect, useState } from "react";
import {
  useAdminRequestApproval,
  useAdminRequestReject,
  useGetAllRequestedAssesmentList,
  useGetAuditorsListForFilters,
  useGetprovidersListForFilters,
  UseGetRfiListForFilters,
} from "../../custom-hooks/queries";
import FIltersDropdown from "../../components/Filters/FiltersDropdown";
import SkeletonLoader from "../../components/SkeletonLoader";
import DropdownMenu from "../../components/Dropdown";
import { dateFormat } from "../../utils/helperMethods";
import Pagination from "../../components/CustomPagination";
import Notification from "../../components/Notification";
import StatusIndicator from "../../components/StatusIndicator";
import { useNavigate } from "react-router-dom";
import CustomTable from "../../components/CustomTable";
import { PageHeader } from "../../components/PageHeader";

export const RequestAssessmentListGrid = () => {
  // Component states for pagination, notifications and sorting
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(21); // default page size (fixed here)
  const [show, setShow] = useState(false);
  const [status, setStatus] = useState("");
  const [sortConfig, setSortConfig] = useState({});

  // Filters (these hooks are used to populate filter options)
  const { data: providers } = useGetprovidersListForFilters();
  const { data: rfis } = UseGetRfiListForFilters();
  const { data: auditors } = useGetAuditorsListForFilters();
  const navigate = useNavigate();
  const [selectedIds, setSelectedIds] = useState({
    auditor: [],
    provider: [],
    status: [],
    services: [],
    sponsor: [],
  });

  // Approval / rejection mutations (for the dropdown actions)
  const {
    mutate: approve,
    error: approveError,
    isError: approveIsError,
  } = useAdminRequestApproval();
  const {
    mutate: reject,
    error: rejectError,
    isError: rejectIsError,
  } = useAdminRequestReject();

  // Filter definitions (adjust totals or options as needed)
  const filters = [
    {
      id: "provider",
      name: "Vendor",
      options: providers,
      total: 1335,
    },

    {
      id: "status",
      name: "Status",
      options: [
        {
          statusID: "Pending",
          label: "Pending",
          checked: false,
        },
        { statusID: "Approved", label: "Approved", checked: false },
        { statusID: "Cancelled", label: "Cancelled", checked: false },
      ],
      total: 5,
    },
  ];

  const menuItems = [{ label: "View", href: "#" }];

  // Use our custom hook to get the assessment list. Notice that we pass in the page size along with the other filters.
  const {
    data: requestList,
    isLoading,
    isError,
    error,
    refetch,
  } = useGetAllRequestedAssesmentList(
    currentPage,
    [],
    selectedIds.provider,
    selectedIds.status,
    [],
    []
  );

  const errorMessage =
    rejectError?.response?.data?.message ||
    approveError?.response?.data?.message ||
    "An unexpected error occurred.";

  // Helper function for natural sorting (correctly sorts strings with numbers)
  const naturalSortCompare = (a, b) => {
    // First, handle empty values
    if (!a && b) return -1;
    if (a && !b) return 1;
    if (!a && !b) return 0;

    // Convert to strings and trim
    const aStr = String(a).trim();
    const bStr = String(b).trim();

    // Split strings into chunks of text and numbers
    const aParts = aStr.split(/(\d+)/).filter(Boolean);
    const bParts = bStr.split(/(\d+)/).filter(Boolean);

    // Compare each chunk
    const minLength = Math.min(aParts.length, bParts.length);

    for (let i = 0; i < minLength; i++) {
      // If both parts are numbers, compare numerically
      if (!isNaN(aParts[i]) && !isNaN(bParts[i])) {
        const numA = parseInt(aParts[i], 10);
        const numB = parseInt(bParts[i], 10);
        if (numA !== numB) {
          return numA - numB;
        }
      }
      // Otherwise compare as strings
      else if (aParts[i] !== bParts[i]) {
        return aParts[i].localeCompare(bParts[i]);
      }
    }

    // If one string is a prefix of the other, the shorter one comes first
    return aParts.length - bParts.length;
  };

  // Toggle sorting for a given key
  const handleSort = (key) => {
    // When clicking on a column, make it the only active sort
    setSortConfig({
      [key]: {
        direction: sortConfig[key]?.direction === "asc" ? "desc" : "asc",
      },
    });
  };

  // Sort status values in a specific order
  const sortStatuses = (aStatus, bStatus, direction) => {
    const statusOrder = ["Approved", "Pending", "Rejected", "Cancelled"];

    const aIndex = statusOrder.indexOf(aStatus);
    const bIndex = statusOrder.indexOf(bStatus);

    if (aIndex !== bIndex) {
      return direction === "asc" ? aIndex - bIndex : bIndex - aIndex;
    }
    return 0;
  };

  // Sort the items returned from the API using the sort configuration
  const sortedItems = [...(requestList?.items || [])].sort((a, b) => {
    // If there's no sort configuration, return default sort
    if (Object.keys(sortConfig).length === 0) return 0;

    // Get the key we're sorting by - there will only be one now with our updated handleSort
    const key = Object.keys(sortConfig)[0];
    const { direction } = sortConfig[key];

    // Special handling for different column types
    if (key === "provider_name") {
      // Handle provider_name sorting with natural sort (for mixed alphanumeric values)
      const valueA = (a.provider_name || "").toLowerCase();
      const valueB = (b.provider_name || "").toLowerCase();

      // Use natural sort for alphanumeric comparison
      const result = naturalSortCompare(valueA, valueB);
      return direction === "asc" ? result : -result;
    } else if (key === "join_vqa_request_status") {
      return sortStatuses(a[key], b[key], direction);
    } else if (
      key === "join_vqa_request_created_on" ||
      key === "assessment_start_date" ||
      key === "agreed_initial_draft_date"
    ) {
      // Date sorting
      const dateA = a[key] ? new Date(a[key]) : null;
      const dateB = b[key] ? new Date(b[key]) : null;

      // Handle null dates
      if (!dateA && dateB) return direction === "asc" ? 1 : -1;
      if (dateA && !dateB) return direction === "asc" ? -1 : 1;
      if (!dateA && !dateB) return 0;

      // Compare dates
      return direction === "asc" ? dateA - dateB : dateB - dateA;
    } else {
      // Default sorting for other columns
      const valueA = a[key];
      const valueB = b[key];

      // Handle empty values
      const isEmptyA = valueA === null || valueA === undefined || valueA === "";
      const isEmptyB = valueB === null || valueB === undefined || valueB === "";

      if (isEmptyA && !isEmptyB) return direction === "asc" ? 1 : -1;
      if (!isEmptyA && isEmptyB) return direction === "asc" ? -1 : 1;
      if (isEmptyA && isEmptyB) return 0;

      // For string comparison
      if (typeof valueA === "string" && typeof valueB === "string") {
        return direction === "asc"
          ? valueA.localeCompare(valueB)
          : valueB.localeCompare(valueA);
      }

      // For other types
      return direction === "asc"
        ? valueA > valueB
          ? 1
          : -1
        : valueA < valueB
        ? 1
        : -1;
    }
  });

  // Reset to first page when any filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedIds]);

  // Define columns for CustomTable
  const columns = [
    {
      key: "provider_name",
      header: "Vendor",
      sortable: true,
      width: "16%",
      render: (item) => (
        <div
          onClick={() =>
            navigate(`/RequestAssesment/${item.join_vqa_request_id}/Details`)
          }
          className="text-gray-500 hover:text-gray-700 hover:cursor-pointer hover:underline"
        >
          {item.provider_name}
        </div>
      ),
    },
    {
      key: "join_vqa_request_created_on",
      header: "Request Date",
      sortable: true,
      width: "16%",
      render: (item) => dateFormat(item.join_vqa_request_created_on),
    },
    {
      key: "assessment_type",
      header: "Assessment Type",
      sortable: true,
      width: "16%",
      render: (item) => (item.audit_type === "remote" ? "Remote" : "On-Site"),
    },
    {
      key: "assessment_start_date",
      header: "VQA Start Date",
      sortable: true,
      width: "16%",
      render: (item) =>
        item.assessment_start_date
          ? dateFormat(item.assessment_start_date)
          : "-",
    },
    {
      key: "agreed_initial_draft_date",
      header: "Agreed Draft Date",
      sortable: true,
      width: "16%",
      render: (item) =>
        item.agreed_initial_draft_date
          ? dateFormat(item.agreed_initial_draft_date)
          : "-",
    },
    {
      key: "join_vqa_request_status",
      header: "Request Status",
      sortable: true,
      width: "16%",
      render: (item) => (
        <StatusIndicator status={item.join_vqa_request_status} />
      ),
    },
    {
      key: "actions",
      header: "Details",
      sortable: false,
      width: "10%",
      render: (item) => (
        <DropdownMenu
          menuItems={menuItems}
          refetch={refetch}
          id={item.join_vqa_request_id}
          approve={approve}
          reject={reject}
          status={setStatus}
          rejectError={rejectError}
          approveError={approveError}
          rejectIsError={rejectIsError}
          approveIsError={approveIsError}
          navigation={`/RequestAssesment/${item.join_vqa_request_id}/Details`}
          icon={true}
          docStatus={item.join_vqa_request_status}
        />
      ),
    },
  ];

  return (
    <div className="container w-full px-4 sm:px-6 lg:px-8 min-h-screen bg-gray-50">
      <PageHeader
        heading="Assessments List"
        subHeading="View and manage assessment requests across your organization."
      />

      <div className="mt-8 flow-root">
        <div className="-mx-4 -my-2 sm:-mx-6 lg:-mx-8">
          <div className="inline-block w-full py-2 align-middle sm:px-6 lg:px-8">
            <div className="mt-3  bg-white shadow-md rounded-md p-4 border border-gray-300">
              <div className="items-center mt-5 px-5">
                {/* Add filters dropdown here if needed */}
                <div className="flex justify-end items-center mb-5">
                  <FIltersDropdown
                    rfis={rfis}
                    providers={providers}
                    setSelectedIds={setSelectedIds}
                    selectedIds={selectedIds}
                    filters={filters}
                  />
                </div>
              </div>

              {isLoading ? (
                <SkeletonLoader />
              ) : (
                <CustomTable
                  columns={columns}
                  data={sortedItems || []}
                  sortConfig={sortConfig}
                  onSort={handleSort}
                  emptyMessage="No assessments found"
                  emptyDescription="No assessment requests are available at this time."
                  isSearchActive={false}
                />
              )}

              {!isError && !isLoading && sortedItems?.length > 0 && (
                <div className="mt-5">
                  <Pagination
                    totalPages={requestList?.total_pages || 1}
                    currentPage={currentPage}
                    onPageChange={setCurrentPage}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <Notification
        show={show}
        setShow={setShow}
        errorMessage={
          rejectError
            ? rejectError?.response?.data?.message
            : approveError?.response?.data?.message
        }
        status={status}
      />
    </div>
  );
};
